import { GridColumnVisibilityModel } from "@mui/x-data-grid-premium";
import { ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { WritableDraft } from "immer";
import { assetService, ingestService } from "../../Core/Api/services";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import { CreateAssetsErrorLog } from "../../Core/Api/Types/create-assets-error-log";
import { CreatePersonalBackupsRequest } from "../../Core/Api/Types/create-personal-backups-request";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import { DbIngestConfigPackInfos } from "../../Core/Api/Types/db-ingest-config-pack-infos";
import { NewRequestField } from "../../Core/Api/Types/db-ingest-config-pack-placeholder-row-field-infos";
import {
  DbIngestConfigPackPlaceholderRowInfos,
  NewRequestRow,
} from "../../Core/Api/Types/db-ingest-config-pack-placeholder-row-infos";
import { DbIngestPersonalBackupInfos } from "../../Core/Api/Types/db-ingest-personal-backup-infos";
import { DbMetadataFieldInfos } from "../../Core/Api/Types/db-metadata-field-infos";
import { DbMetadataFieldValueInfos } from "../../Core/Api/Types/db-metadata-field-value-infos";
import { GetGraphResponse } from "../../Core/Api/Types/get-graph-response";
import { GetPlaceholderResponse } from "../../Core/Api/Types/get-placeholder-response";
import {
  IngestConfigPackColumnSet,
  NewRequestColumn,
  NewRequestColumnGroup,
} from "../../Core/Api/Types/ingest-config-pack-column-set";
import { ObjectClass } from "../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";
import { ObjectType } from "../../Core/Api/Types/object-type";
import {
  AddNewMetadataToAsset,
  CreateNewDbMetadataValue,
  GetMetadataByTagName,
} from "../../Core/Utils/asset-object-utils";
import GlobalConst from "../../Core/Utils/Constants/globals";
import { Distinct, DownloadFile, FindDuplicates, GroupByToMap } from "../../Utils/helpers";
import { RootState } from "../redux-store";

// #region models

export interface FiltersVisibilityModel {
  columnId: number;
  columnFieldName: string;
  show: boolean;
}

export interface FieldValueModel {
  columnMetadataFieldId: number;
  placeholderRowId: number;
  value: any;
}

export interface TitleTopology {
  id: number;
  name: string;
  contents: ContentTopology[];
  isSeason: boolean;
  totalEpisodes: number;
}

export interface ContentTopology {
  name: string;
  isNew: boolean;
  assets: AssetTopology[];
}

export interface AssetTopology {
  id: number;
  placeholderRowId: number;
  type: ObjectType;
  name: string;
  isNew: boolean;
}

interface GroupErrorModel {
  groupName: string;
  isErrored: boolean;
}

interface ColumnErrorModel {
  columnMetadataFieldId: number;
  errorNumber: number;
}

interface GoupedRowErrorModel {
  columnMetadataFieldIds: number[];
  placeholderRowId: number;
  errorNumber: number;
}

interface NewRequestProgress {
  progress: number;
  total: number;
}

interface NewRequestEpisodesSelection {
  titleId: number;
  totalEpisodes: number;
  selectedEpisodes: number[];
}

interface LinkedMetasIdsDictionary {
  [index: number]: number[];
}

export interface NewRequestSliceData {
  isLoading: boolean;
  isMarketplaceLoading: boolean;
  // panel data selection
  selectedTitles: DbAssetObjectInfos[];
  fetchedPacks: DbIngestConfigPackInfos[];
  selectedPack: DbIngestConfigPackInfos | undefined;
  fetchedColumns: IngestConfigPackColumnSet[];
  // placeholder management
  placeholders: DbIngestConfigPackPlaceholderRowInfos[];
  // computedColumnGroups
  columnGroups: NewRequestColumnGroup[];
  rows: NewRequestRow[];
  fields: NewRequestField[];
  linkedMetas: LinkedMetasIdsDictionary;
  // errors
  groupErrors: GroupErrorModel[];
  columnErrors: ColumnErrorModel[];
  groupedRowErrors: GoupedRowErrorModel[];
  // tabs, filters and visibility model
  selectedTabValue: string | undefined;
  filtersColumnVisibilityMask: FiltersVisibilityModel[];
  tableVisibilityModel: GridColumnVisibilityModel;
  // assets
  generatedAssets: DbAssetObjectInfos[];
  selectedEpisodes: NewRequestEpisodesSelection[];
  creationProgress: NewRequestProgress;
  creationErrors: CreateAssetsErrorLog[];
  errorMessage: string;
  // config load/save
  configBackups: DbIngestPersonalBackupInfos[];
}
// #endregion models

const initialState: NewRequestSliceData = {
  isLoading: false,
  isMarketplaceLoading: false,
  selectedTitles: [],
  fetchedPacks: [],
  selectedPack: undefined,
  fetchedColumns: [],
  placeholders: [],
  columnGroups: [],
  rows: [],
  fields: [],
  linkedMetas: {},
  groupErrors: [],
  columnErrors: [],
  groupedRowErrors: [],
  selectedTabValue: undefined,
  filtersColumnVisibilityMask: [],
  tableVisibilityModel: {},
  generatedAssets: [],
  selectedEpisodes: [],
  creationProgress: { progress: 0, total: 0 },
  creationErrors: [],
  errorMessage: "",
  configBackups: [],
};

// function that init columnGroups of DataTable from pack layout
const initColumnGroups = (
  columnSet: IngestConfigPackColumnSet[]
): { groups: NewRequestColumnGroup[]; linkedMetas: LinkedMetasIdsDictionary } => {
  let groups: NewRequestColumnGroup[] = [];
  let linkedMetas: LinkedMetasIdsDictionary = {};

  // sort columns by order and group
  const sortedColumnSet = [...columnSet].sort((a, b) => {
    if (a.columnFieldInfos!.group && b.columnFieldInfos!.group) {
      return a.columnInfos!.order! - b.columnInfos!.order!;
    } else {
      if (a.columnFieldInfos!.group) return -1;
      else if (b.columnFieldInfos!.group) return 1;
      else return 0;
    }
  });

  // group columns by group name
  const groupedColumns = GroupByToMap(sortedColumnSet, (c) => c.columnFieldInfos?.group);
  groupedColumns.forEach((value, key) => {
    // build group's columns array
    const columns = value.map((column) => {
      if (column.columnInfos?.dependencies && column.columnInfos.dependencies.length > 0) {
        for (var dep of column.columnInfos.dependencies) {
          if (!dep.fieldId) continue;

          if (!linkedMetas[dep.fieldId]) linkedMetas[dep.fieldId] = [column.columnInfos!.fieldId || 0];
          else linkedMetas[dep.fieldId].push(column.columnInfos!.fieldId || 0);
        }
      }
      return {
        daletDataFieldTagName: column.columnInfos!.daletDataFieldTagName || undefined,
        dependancyDaletDataFieldTagName: column.columnInfos!.dependancyDaletDataFieldTagName || undefined,
        isMandatory: column.columnInfos!.isMandatory || false,
        isHidden: column.columnInfos!.isHidden || false,
        isToFillDuringIngest: column.columnInfos!.isToFillDuringIngest || false,
        isEditable: column.columnInfos!.isEditable || false,
        order: column.columnInfos!.order || 0,
        fieldId: column.columnInfos!.fieldId || 0,
        platformId: column.columnInfos!.platformId || 0,
        label: column.columnInfos!.label || "",
        dependencies: column.columnInfos!.dependencies,

        metadata: column.columnFieldInfos!,
      };
    });
    // compute if group is shown (at least one column is visible)
    const showGroup = columns.some(isColumnDefaultVisible);
    // add group to output
    groups.push({
      name: key || "Other",
      columns,
      show: showGroup,
    });
  });
  return { groups, linkedMetas };
};

// function that init errors array for datatable
const initErrors = (
  columnGroups: NewRequestColumnGroup[],
  rows: NewRequestRow[],
  fields: NewRequestField[]
): { groupsErrors: GroupErrorModel[]; columnsErrors: ColumnErrorModel[]; rowErrors: GoupedRowErrorModel[] } => {
  let groupsErrors: GroupErrorModel[] = [];
  let columnsErrors: ColumnErrorModel[] = [];
  let rowErrors: GoupedRowErrorModel[] = [];

  // for each column, compute number of errored fields
  columnGroups.forEach((group) => {
    let groupColumnsErrors: ColumnErrorModel[] = [];
    group.columns.forEach((column) => {
      groupColumnsErrors.push({
        columnMetadataFieldId: column.fieldId,
        errorNumber: fields.reduce(
          (count, field) => (field.columnMetadataFieldId === column.fieldId && field.isErrored ? ++count : count),
          0
        ),
      });
    });
    groupsErrors.push({
      groupName: group.name,
      isErrored: groupColumnsErrors.some((gce) => gce.errorNumber > 0),
    });
    columnsErrors = columnsErrors.concat(groupColumnsErrors);
  });

  // for each column group of row, count number of errored fields
  rows.forEach((row) => {
    columnGroups.forEach((group) => {
      const groupColumnFieldIds = group.columns.flatMap((c) => c.fieldId);

      rowErrors.push({
        columnMetadataFieldIds: groupColumnFieldIds,
        placeholderRowId: row.id,
        errorNumber: fields.reduce(
          (count, field) =>
            field.placeholderRowId === row.id &&
            groupColumnFieldIds.includes(field.columnMetadataFieldId) &&
            field.isErrored
              ? ++count
              : count,
          0
        ),
      });
    });
  });

  return { groupsErrors, columnsErrors, rowErrors };
};

// method that compute all errors of field and linked fields. Also show linked columns if errored
const computeAllErrors = (state: WritableDraft<NewRequestSliceData>, fieldIndex: number) => {
  // compute field errors
  computeErrors(state, fieldIndex);
  // get field for next step
  const newField = state.fields[fieldIndex];
  // if column is linked to others
  if (state.linkedMetas[newField.columnMetadataFieldId]?.length > 0) {
    // loop over linked columns
    for (let i = 0; i < state.linkedMetas[newField.columnMetadataFieldId].length; i++) {
      // retrieve column id and index to process field
      const columnId = state.linkedMetas[newField.columnMetadataFieldId][i];
      const indexOfFieldToProcess = state.fields.findIndex(
        (f) => f.placeholderRowId === newField.placeholderRowId && f.columnMetadataFieldId === columnId
      );
      // if index
      if (indexOfFieldToProcess > -1) {
        // compute errors
        computeErrors(state, indexOfFieldToProcess);
        // if field is errored
        if (state.fields[indexOfFieldToProcess].isErrored) {
          // show column, because we cant to see the error in table
          const indexOfColumnVisibilityMask = state.filtersColumnVisibilityMask.findIndex(
            (m) => m.columnId === state.fields[indexOfFieldToProcess].columnMetadataFieldId
          );
          if (indexOfColumnVisibilityMask > -1 && !state.filtersColumnVisibilityMask[indexOfColumnVisibilityMask].show)
            toggleColumnShow(
              state,
              state.fields[indexOfFieldToProcess].columnMetadataFieldId,
              indexOfColumnVisibilityMask
            );
        }
      }
    }
  }
};

// for a given field index, compute errors for its column and row grouped columns
const computeErrors = (state: WritableDraft<NewRequestSliceData>, fieldIndex: number) => {
  // get field column
  // const fieldColumn = state.columnGroups.flatMap(g => g.columns).find()

  // get field is errored
  const isFieldErrored = computeFieldIsErroredAdvancedFromState(state, state.fields[fieldIndex]);

  // if different
  if (isFieldErrored !== state.fields[fieldIndex].isErrored) {
    // set error
    state.fields[fieldIndex].isErrored = isFieldErrored;
    // find indexes
    const indexOfColumnErrorModel = state.columnErrors.findIndex(
      (c) => c.columnMetadataFieldId === state.fields[fieldIndex].columnMetadataFieldId
    );
    const indexOfRowErrorModel = state.groupedRowErrors.findIndex(
      (r) =>
        r.placeholderRowId === state.fields[fieldIndex].placeholderRowId &&
        r.columnMetadataFieldIds.includes(state.fields[fieldIndex].columnMetadataFieldId)
    );
    // increment or decrement errors
    if (indexOfColumnErrorModel > -1)
      isFieldErrored
        ? ++state.columnErrors[indexOfColumnErrorModel].errorNumber
        : --state.columnErrors[indexOfColumnErrorModel].errorNumber;

    if (indexOfRowErrorModel > -1)
      isFieldErrored
        ? ++state.groupedRowErrors[indexOfRowErrorModel].errorNumber
        : --state.groupedRowErrors[indexOfRowErrorModel].errorNumber;

    // groupErrors
    state.columnGroups.forEach((columnGroup) => {
      const groupErrorModelIndex = state.groupErrors.findIndex((g) => g.groupName === columnGroup.name);
      if (groupErrorModelIndex > -1) {
        const columnGroupcolumnsIds = columnGroup.columns.map((column) => column.fieldId);
        state.groupErrors[groupErrorModelIndex].isErrored = state.columnErrors.some(
          (c) => columnGroupcolumnsIds.includes(c.columnMetadataFieldId) && c.errorNumber > 0
        );
      }
    });
  }
};

// compute meta with restricted values and compute initial value of field
const getComputedMetaAndInitialValue = (
  rowPredefinedValues: string[] | null,
  columnMetadata: DbMetadataFieldInfos | undefined
): { value: any; metadata: DbMetadataFieldInfos | undefined; isValueSettable: boolean } => {
  if (!columnMetadata) return { value: null, metadata: undefined, isValueSettable: false };

  let value: any = undefined;
  let metadata = { ...new DbMetadataFieldInfos({ ...columnMetadata }) }; // use this to have an object and not a class cause Redux doesn't like classes (not fully serializable due to constructor)

  let isValueSettable = true;

  // objectClass special case => values are from enum
  if (metadata.tagName! === "objectClass") {
    metadata.values = Object.keys(ObjectClass).map((key) => ({
      ...new DbMetadataFieldValueInfos({
        id: metadata.id,
        label: ObjectClass[key],
        value: key,
        valueObject: CreateNewDbMetadataValue(metadata.id, metadata.baseType!, metadata.tagName!, key),
      }),
    }));
  }
  // objectType special case => values are from enum
  if (metadata.tagName! === "objectType") {
    metadata.values = Object.keys(ObjectType).map((key) => ({
      ...new DbMetadataFieldValueInfos({
        id: metadata.id,
        label: ObjectType[key],
        value: key,
        valueObject: CreateNewDbMetadataValue(metadata.id, metadata.baseType!, metadata.tagName!, key),
      }),
    }));
  }
  // get predefined values
  const predefinedValues: DbMetadataFieldValueInfos[] = [];
  if (rowPredefinedValues)
    for (let i = 0; i < rowPredefinedValues.length; i++) {
      const value = metadata.values?.find((v) => v.value === rowPredefinedValues[i]);
      if (value) predefinedValues.push(value);
    }

  // if no predefined value
  if (!predefinedValues || predefinedValues.length === 0) {
    if (metadata.baseType === BaseMetadataType.DbBoolMetadataValueInfos)
      // set value of bool if it's not defined (can't have null or undefined as bool so default is false)
      value = metadata.values?.find((x) => x.value === "False") || null;
  } else {
    // set first predefined value
    if (
      metadata.baseType !== BaseMetadataType.DbLanguageCodeMultiMetadataValueInfos &&
      metadata.baseType !== BaseMetadataType.DbStringMultiMetadataValueInfos &&
      metadata.baseType !== BaseMetadataType.DbLongMultiMetadataValueInfos &&
      metadata.baseType !== BaseMetadataType.DbDoubleMultiMetadataValueInfos
    )
      value = predefinedValues[0];
    else if (predefinedValues.length === 1) value = predefinedValues[0];
    else value = predefinedValues;

    // set values as predefined values to all non bool metas (bool only have 2 options, so predefined values only refers to the one selected by default)
    if (metadata.baseType !== BaseMetadataType.DbBoolMetadataValueInfos) {
      metadata = { ...new DbMetadataFieldInfos({ ...metadata, values: predefinedValues }) };
      // value isn't settable if we have only one predefined value
      if (predefinedValues.length === 1) {
        isValueSettable = false;
      }
    }
  }

  return { value, metadata, isValueSettable };
};

// returns boolean representing if the field alone is errored
const computeFieldIsErroredBasic = (field: NewRequestField): boolean => {
  return field.isApplicable && field.isMandatory && (!field.value || field.value === ""); // TODO : check if empty
};

// returns boolean representing if the field, while taking other fields too, is errored (linked fields)
const computeFieldIsErroredAdvanced = (
  field: NewRequestField,
  otherFields: NewRequestField[],
  columns: NewRequestColumn[]
): boolean => {
  const column = columns.find((c) => c.fieldId === field.columnMetadataFieldId);
  if (!column || !column.dependencies?.length || !field.isApplicable) return computeFieldIsErroredBasic(field);
  for (let dep of column.dependencies) {
    const linkedField = otherFields.find(
      (c) => c.placeholderRowId === field.placeholderRowId && c.columnMetadataFieldId === dep.fieldId
    );
    const linkedValue = linkedField?.value?.value;
    if (!linkedValue || !dep.activeForValues?.includes(field.value)) return computeFieldIsErroredBasic(field);
    if (dep.values && dep.values.length > 0 && !dep.values?.includes(linkedValue)) return false;
  }
  return field.value === undefined || field.value === null;
};

const computeFieldIsErroredAdvancedFromState = (state: WritableDraft<NewRequestSliceData>, field: NewRequestField) => {
  const placeholderFields = state.fields.filter((f) => f.placeholderRowId === field.placeholderRowId);
  const columns = state.columnGroups.flatMap((g) => g.columns);

  return computeFieldIsErroredAdvanced(field, placeholderFields, columns);
};

// compute table rows and field from given placeholder
const getRowsAndFieldsFromPlaceholderRows = (
  columnGroups: NewRequestColumnGroup[],
  rows: DbIngestConfigPackPlaceholderRowInfos[],
  originalRowId?: number
): { rows: NewRequestRow[]; fields: NewRequestField[] } => {
  let rowsOutput: NewRequestRow[] = [];
  let fieldsOutput: NewRequestField[] = [];

  // get columns
  const columns = columnGroups.flatMap((g) => g.columns);

  //loop on rows
  rows.forEach((row) => {
    let localFieldsOutput: NewRequestField[] = [];
    // create row
    const newRow: NewRequestRow = {
      id: row.id,
      originalPlaceholderId: originalRowId ?? row.id,
      placeHolderName: row.name!,
      isOptionnal: row.isOptionnal || false,
      isOutdated: false,
    };
    // loop on row's fields
    row.fields?.forEach((field) => {
      if (field) {
        // get column
        const column = columns.find((c) => c.fieldId === field.columnMetadataFieldId); // TODO : maybe use dictionary for this

        const computedProps = getComputedMetaAndInitialValue(
          field.predefinedValues,
          field.metadataField || column?.metadata
        );

        // create field
        const newField: NewRequestField = {
          columnMetadataFieldId: field.columnMetadataFieldId!,
          placeholderRowId: row.id,
          isApplicable: field.isApplicable!,
          isMandatory: (column?.isMandatory || false) && !field.overrideIsNotMandatory,
          predefinedValues: field.predefinedValues || [],
          isErrored: false,
          ...computedProps,
        };
        newField.isErrored = computeFieldIsErroredBasic(newField);
        // add field to row (for table)
        if (computedProps.metadata) {
          newRow[computedProps.metadata.tagName!] = newField;
          // add field to array (for redux)
          localFieldsOutput.push(newField);
        } else console.error(`Metadata not found for row id=${row.id} and field id=${field.id}`);
      }
    });

    // loop over fields (with default value setted)
    localFieldsOutput.forEach((field) => {
      // compute error with related fields
      field.isErrored = computeFieldIsErroredAdvanced(field, localFieldsOutput, columns);
    });

    fieldsOutput.push(...localFieldsOutput);
    localFieldsOutput = [];

    // add row to array (for both table and redux)
    rowsOutput.push(newRow);
  });
  return { rows: rowsOutput, fields: fieldsOutput };
};

const isColumnDefaultVisible = (column: NewRequestColumn): boolean => {
  return column.order < 0 || column.isMandatory || !column.isHidden;
};

// This computes initial visibility mask, used when pressing "set default" in filters panel
const initFiltersColumnVisibilityMask = (columnGroups: NewRequestColumnGroup[]): FiltersVisibilityModel[] => {
  const output: FiltersVisibilityModel[] = [];
  const maxColumnId = Math.max(...columnGroups.flatMap((g) => g.columns.flatMap((c) => c.fieldId)));

  let groupIterations = 0;
  columnGroups.forEach((group) => {
    let showGroup = false;
    group.columns.forEach((column) => {
      const showColumn = isColumnDefaultVisible(column);
      output.push({
        columnId: column.fieldId,
        columnFieldName: column.metadata.tagName!,
        show: showColumn,
      });
      if (showColumn) showGroup = true;
    });
    output.push({
      columnId: maxColumnId + ++groupIterations,
      columnFieldName: group.name,
      show: showGroup,
    });
  });
  return output;
};

// with columns visibility mask, we compute GridColumnVisibilityModel used by Mui table
// this computes visibility based on active tab and filters panel inputs
const computeVisibilityModel = (
  columnGroups: NewRequestColumnGroup[],
  filtersColumnVisibilityMask: FiltersVisibilityModel[],
  selectedTabValue: string
): GridColumnVisibilityModel => {
  let output: GridColumnVisibilityModel = {};
  columnGroups.forEach((group) => {
    output[group.name] = group.show;
    group.columns.forEach((column) => {
      output[column.metadata.tagName!] =
        (group.name === selectedTabValue || selectedTabValue === "all") &&
        filtersColumnVisibilityMask.find((f) => f.columnFieldName === column.metadata.tagName!)!.show;
    });
  });
  return output;
};

// function that manage the toggle to show a column
const toggleColumnShow = (
  state: WritableDraft<NewRequestSliceData>,
  columnFieldId: number,
  filtersColumnIndex?: number
) => {
  // get column index
  const _filtersColumnIndex =
    filtersColumnIndex ?? state.filtersColumnVisibilityMask.findIndex((c) => c.columnId === columnFieldId);
  // if column exists
  if (_filtersColumnIndex > -1) {
    // set column visibility
    state.filtersColumnVisibilityMask[_filtersColumnIndex].show =
      !state.filtersColumnVisibilityMask[_filtersColumnIndex].show;
    // get group index
    const indexOfGroup = state.columnGroups.findIndex((g) =>
      g.columns.flatMap((c) => c.fieldId).includes(columnFieldId)
    );
    // get group's columns field names
    const groupColumnsFieldNames = state.columnGroups[indexOfGroup].columns.flatMap((c) => c.metadata.tagName!);
    // get relevant filter panel columns of group
    const filtersColumnsOfGroups = state.filtersColumnVisibilityMask.filter((c) =>
      groupColumnsFieldNames.includes(c.columnFieldName)
    );
    // count number of shown columns, if > 0 show, else hide
    const showGroup = filtersColumnsOfGroups.reduce((count, column) => (column.show ? ++count : count), 0) > 0;
    // set show on group
    state.columnGroups[indexOfGroup].show = showGroup;
    // if hide column group, and if it is the selected one, set selected to first show
    if (!showGroup && state.selectedTabValue === state.columnGroups[indexOfGroup].name)
      state.selectedTabValue = state.columnGroups.find((g) => g.show)?.name || "";
    // compute visibility model
    state.tableVisibilityModel = computeVisibilityModel(
      state.columnGroups,
      state.filtersColumnVisibilityMask,
      state.selectedTabValue!
    );
  }
};

const toggleColumnsVisibilityForLinkedMetas = (state: WritableDraft<NewRequestSliceData>) => {
  Object.keys(state.linkedMetas)
    .map(Number)
    .forEach((key) => {
      let showLinkedColumn = false;
      state.linkedMetas[key].forEach((columnId) => {
        // find index of mask
        const columnVisibilityMaskIndex = state.filtersColumnVisibilityMask.findIndex((m) => m.columnId === columnId);
        // if index and not show
        if (columnVisibilityMaskIndex > -1 && !state.filtersColumnVisibilityMask[columnVisibilityMaskIndex].show) {
          // then get fields
          const linkedFields = state.fields.filter((f) => f.columnMetadataFieldId === columnId);
          // if at least one field is errored, show column
          if (linkedFields.some((f) => f.isErrored)) {
            toggleColumnShow(state, columnId, columnVisibilityMaskIndex);
            showLinkedColumn = true;
          }
        }
      });
      // show "parent" one if at least one linked column has to be shown
      if (showLinkedColumn) toggleColumnShow(state, key);
    });
};

const updateFieldValueFromOtherField = (
  state: WritableDraft<NewRequestSliceData>,
  fieldToCopy: NewRequestField,
  valueToCopy: any,
  index: number
) => {
  if (
    Array.isArray(valueToCopy) &&
    (fieldToCopy.metadata?.baseType === BaseMetadataType.DbLanguageCodeMultiMetadataValueInfos ||
      fieldToCopy.metadata?.baseType === BaseMetadataType.DbStringMultiMetadataValueInfos ||
      fieldToCopy.metadata?.baseType === BaseMetadataType.DbLongMultiMetadataValueInfos ||
      fieldToCopy.metadata?.baseType === BaseMetadataType.DbDoubleMultiMetadataValueInfos)
  ) {
    let newValue: any[] = [];
    if (!state.fields[index].predefinedValues || state.fields[index].predefinedValues.length === 0)
      newValue = valueToCopy;
    else
      for (let i = 0; i < valueToCopy.length; i++) {
        const value = valueToCopy[i];
        if (state.fields[index].predefinedValues.includes(value.value)) newValue.push(value);
      }

    if (newValue.length > 0) {
      state.fields[index].value = newValue;
      computeAllErrors(state, index);
    }
    // field not multi
  } else {
    // if value can be paste
    if (
      !state.fields[index].predefinedValues ||
      state.fields[index].predefinedValues.length === 0 ||
      state.fields[index].predefinedValues.includes(valueToCopy.value)
    ) {
      // paste value in field
      state.fields[index].value = valueToCopy;
      // compute errors
      computeAllErrors(state, index);
    }
  }
};

const getFieldValue = (field: NewRequestField) => {
  let val: any = null;
  if (field.metadata)
    // switch on meta type
    switch (field.metadata.baseType) {
      // number
      case BaseMetadataType.DbLongMetadataValueInfos:
      case BaseMetadataType.DbDoubleMetadataValueInfos:
        val = Number(field.value?.value || field.value);
        break;
      // language
      case BaseMetadataType.DbLanguageCodeMetadataValueInfos:
        val = field.value?.valueObject?.value;
        break;
      // boolean
      case BaseMetadataType.DbBoolMetadataValueInfos:
        val = field.value?.value === true || field.value?.value === "True" || field.value?.value === "true";
        break;
      // string multiple
      case BaseMetadataType.DbStringMultiMetadataValueInfos:
        if (!Array.isArray(field.value)) val = [field.value?.value ?? field.value];
        else val = field.value.map((value) => value.value?.value ?? value?.value);
        break;
      // number multiple
      case BaseMetadataType.DbLongMultiMetadataValueInfos:
      case BaseMetadataType.DbDoubleMultiMetadataValueInfos:
        if (!Array.isArray(field.value)) val = [Number(field.value?.value ?? field.value)];
        else val = field.value.map((value) => value.value?.value ?? value.value);
        break;
      case BaseMetadataType.DbDateTimeMetadataValueInfos:
        if (typeof field.value !== typeof Date) val = new Date(field.value);
        else val = field.value;
        break;
      // string and others
      default:
        val = field.value?.value || field.value;
        break;
    }
  return val;
};

const setSelectedTitlesToState = (
  state: WritableDraft<NewRequestSliceData>,
  action: PayloadAction<DbAssetObjectInfos[]>
) => {
  state.selectedTitles = action.payload;

  // compute episodes selection of title (0 if movie)
  let selectedEpisodes: NewRequestEpisodesSelection[] = [];
  for (let i = 0; i < action.payload.length; i++) {
    const title = action.payload[i];
    const totalEpisodes = GetMetadataByTagName(title, "serieSeasonEpisodeTotalNumber")?.value || 0;
    selectedEpisodes.push({
      titleId: title.id,
      totalEpisodes: totalEpisodes,
      selectedEpisodes: Array.from(
        {
          length: totalEpisodes,
        },
        (item, index) => (item = index + 1)
      ),
    });
  }
  state.selectedEpisodes = selectedEpisodes;
};

export const FetchLightPackPlaceholders: any = createAsyncThunk(
  "newRequest/FetchLightPackPlaceholders",
  async (selectedPackId: number, thunkApi): Promise<DbIngestConfigPackPlaceholderRowInfos[]> => {
    thunkApi.dispatch(SetIsMarketplaceLoading(true));

    return (
      (
        await ingestService.GetFilteredPlaceholders({
          aggregateAll: false,
          filterByIds: null,
          onlyNonOptionnal: false,
          packId: selectedPackId,
        })
      ).placeholders || []
    );
  }
);

export const FetchNonOptionalPlaceholder: any = createAsyncThunk(
  "newRequest/FetchNonOptionalPlaceholder",
  async (selectedPackId: number): Promise<DbIngestConfigPackPlaceholderRowInfos[]> => {
    return (
      (
        await ingestService.GetFilteredPlaceholders({
          aggregateAll: true,
          filterByIds: null,
          onlyNonOptionnal: true,
          packId: selectedPackId,
        })
      ).placeholders || []
    );
  }
);

export const FetchPackAndTitlesThenFillWithBackup: any = createAsyncThunk(
  "newRequest/FetchPackAndFillWithBackup",
  async (
    backup: DbIngestPersonalBackupInfos,
    thunkApi
  ): Promise<{ placeholders: DbIngestConfigPackPlaceholderRowInfos[]; backupId: number }> => {
    thunkApi.dispatch(SelectPackById(backup.packId || 0));
    thunkApi.dispatch(FetchLightPackPlaceholders(backup.packId || 0));

    // Construct titles promises
    const titlesPromisesArray: Promise<GetGraphResponse>[] = [];
    for (let i = 0; i < (backup.titleIds?.length || 0); i++) {
      titlesPromisesArray.push(assetService.GetObjectById(backup.titleIds![i]));
    }
    // Fetch titles async and set them when all are fetched
    Promise.all(titlesPromisesArray).then((allResponses) => {
      const titles = allResponses.filter((r) => r.graph).map((r) => r.graph!);
      thunkApi.dispatch(SetSelectedTitles(titles));
    });

    // Construct placeholders promises
    const placeholderPromisesArray: Promise<GetPlaceholderResponse>[] = [];
    backup.placeholderRowsSelected
      ?.map((x) => x.rowId)
      .filter(Distinct)
      .forEach((placeholderRowId) => {
        placeholderPromisesArray.push(ingestService.GetPlaceholderById(placeholderRowId));
      });

    const allPromisesResults = await Promise.all(placeholderPromisesArray);

    return {
      placeholders: allPromisesResults.filter((r) => r.placeholder).map((r) => r.placeholder!),
      backupId: backup.id,
    };
  }
);

export const FetchSelectedTitles: any = createAsyncThunk(
  "newRequest/FetchSelectedTitles",
  async (titles: DbAssetObjectInfos[]): Promise<DbAssetObjectInfos[]> => {
    const output: DbAssetObjectInfos[] = [];
    const titlesPromisesArray: Promise<GetGraphResponse>[] = [];
    for (let i = 0; i < titles.length; i++) {
      if (titles[i].childObjects && titles[i].childObjects?.length)
        output.push(titles[i]); // if childrens already fetched
      else titlesPromisesArray.push(assetService.GetObjectById(titles[i].id));
    }

    const responses = await Promise.all(titlesPromisesArray);
    output.push(...responses.filter((x) => x.graph).map((x) => x.graph!));
    return output;
  }
);

export const FetchFullPlaceholdersByIds: any = createAsyncThunk(
  "newRequest/FetchFullPlaceholdersByIds",
  async (selectedPlaceholdersIds: number[], thunkApi): Promise<DbIngestConfigPackPlaceholderRowInfos[]> => {
    const packId = (thunkApi.getState() as RootState).newRequestSlice.selectedPack?.id;
    if (!packId) return [];

    return (
      (
        await ingestService.GetFilteredPlaceholders({
          aggregateAll: true,
          filterByIds: selectedPlaceholdersIds,
          onlyNonOptionnal: false,
          packId: packId,
        })
      ).placeholders || []
    );
  }
);
// creates requests asynchronously from generated assets
export const CreateRequests: any = createAsyncThunk("newRequest/CreateRequests", async (_, thunkApi) => {
  // get state
  const state = (thunkApi.getState() as RootState).newRequestSlice;
  // get titles, assets and selected episodes (empty array if title is movie)
  const titles = state.selectedTitles;
  const generatedAssets = state.generatedAssets;
  const selectedEpisodes = state.selectedEpisodes;

  // loop over titles
  for (let i = 0; i < titles.length; i++) {
    const title = titles[i];
    // loop over assets
    for (let j = 0; j < generatedAssets.length; j++) {
      const asset = { ...generatedAssets[j] }; // without this, setting asset parentTitleId buggs and send us in noop() function... => this is due to generatedAssets being readonly, though it doesn't throw an exception

      asset.parentTitleId = title.id;

      // TODO : try to make calls this way, reduce animation time of progress bar to prevent flicking
      //   assetService
      //     .CreateByTemplate([
      //       {
      //         obj: asset,
      //         episodeNumbers: [],
      //         uploadableFileFormatIds: [],
      //         isTest: false,
      //       },
      //     ])
      //     .then((response) => {
      //       thunkApi.dispatch(IncrementProgress());

      //       if (!response.success && response.errors) {
      //         for (let k = 0; k < response.errors.length; k++) {
      //           response.errors[k]["id"] = `${i}${j}`;
      //         }
      //         thunkApi.dispatch(AddCreateError(response.errors));
      //       }
      //     })
      //     .catch((response) => {
      //       thunkApi.dispatch(IncrementProgress());
      //       // Here it's errors from back like HTTP 400 etc
      //     });

      // post to api
      const response = await assetService.CreateByTemplate([
        {
          obj: asset,
          episodeNumbers: selectedEpisodes.find((s) => s.titleId === title.id)?.selectedEpisodes || [],
          uploadableFileFormatIds: [],
          isTest: false,
        },
      ]);
      // increment progress
      thunkApi.dispatch(IncrementProgress());
      // add id to error, cause mui datagrid needs it (id computed from index)
      if (!response.success && response.errors) {
        for (let k = 0; k < response.errors.length; k++) {
          response.errors[k]["id"] = `${i}-${j}-${k}`;
          response.errors[k].assetNumber = j;
        }
        // dispatch errors
        thunkApi.dispatch(AddCreateError(response.errors));
        thunkApi.dispatch(AddCreateErrorMessage(response.message || "Undefined error"));
      }
    }
  }
});

export const RefreshBackups: any = createAsyncThunk("newRequest/RefreshBackups", async (_) => {
  const backups = (await ingestService.GetMyPersonalBackups()).backups;
  return backups;
});

const newRequestSlice = createSlice({
  name: "newRequest",
  initialState,
  reducers: {
    // isLoading
    SetIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    SetIsMarketplaceLoading: (state, action: PayloadAction<boolean>) => {
      state.isMarketplaceLoading = action.payload;
    },
    // Titles management
    SetSelectedTitles: (state, action: PayloadAction<DbAssetObjectInfos[]>) => setSelectedTitlesToState(state, action),
    RemoveTitleByName: (state, action: PayloadAction<string>) => {
      state.selectedTitles = state.selectedTitles.filter((t) => t.name !== action.payload);
    },
    // Packs management
    SetFetchedPacks: (state, action: PayloadAction<DbIngestConfigPackInfos[]>) => {
      state.fetchedPacks = action.payload;
    },
    SetFetchedColumnGroups: (state, action: PayloadAction<IngestConfigPackColumnSet[]>) => {
      state.fetchedColumns = action.payload;

      const newColumnSet = action.payload.filter(
        (c) => c.columnFieldInfos?.tagName !== "isSelected" && c.columnFieldInfos?.tagName !== "name"
      );

      // init column groups and filters model
      const { groups, linkedMetas } = initColumnGroups(newColumnSet);
      const filtersModel = initFiltersColumnVisibilityMask(groups);
      // get first not hidden group name
      const selectedTab = groups.find((g) => g.show)?.name || "";

      state.selectedTabValue = selectedTab;
      state.columnGroups = groups;
      state.linkedMetas = linkedMetas;
      state.filtersColumnVisibilityMask = filtersModel;
      state.tableVisibilityModel = computeVisibilityModel(groups, filtersModel, selectedTab);
    },
    SelectPackById: (state, action: PayloadAction<number>) => {
      state.selectedPack = state.fetchedPacks.find((p) => p.id === action.payload);

      // hard reset some of props in state (in case we switch from another pack)
      state.isLoading = true;
      state.placeholders = initialState.placeholders;
      state.rows = initialState.rows;
      state.fields = initialState.fields;
      state.columnErrors = initialState.columnErrors;
      state.groupedRowErrors = initialState.groupedRowErrors;
      state.generatedAssets = initialState.generatedAssets;
      state.creationProgress = initialState.creationProgress;
      state.creationErrors = initialState.creationErrors;
      state.errorMessage = initialState.errorMessage;
      const selectedTab = state.columnGroups.find((g) => g.show)?.name || "";
      const filtersModel = initFiltersColumnVisibilityMask(state.columnGroups);
      state.selectedTabValue = selectedTab;
      state.filtersColumnVisibilityMask = filtersModel;
      state.tableVisibilityModel = computeVisibilityModel(state.columnGroups, filtersModel, selectedTab);
    },
    UnselectPack: (state) => {
      state.selectedPack = undefined;
    },
    UnselectPlaceholderById: (state, action: PayloadAction<number>) => {
      // get placeholder
      const rowId = action.payload;
      const rowIndex = state.rows.findIndex((r) => r.id === rowId);
      if (rowIndex > -1) {
        // remove row
        state.rows.splice(rowIndex, 1);
        // save fields to remove
        const deletedFields = state.fields.filter((f) => f.placeholderRowId === rowId);
        // remove fields
        state.fields = state.fields.filter((f) => f.placeholderRowId !== rowId);

        // compute errors
        state.groupedRowErrors = state.groupedRowErrors.filter((g) => g.placeholderRowId !== rowId);
        deletedFields.forEach((field) => {
          if (field.isErrored) {
            const indexOfColumnError = state.columnErrors.findIndex(
              (c) => c.columnMetadataFieldId === field.columnMetadataFieldId
            );
            if (indexOfColumnError > -1) --state.columnErrors[indexOfColumnError].errorNumber;
          }
        });
        state.groupErrors.forEach((group) => {
          const columnGroup = state.columnGroups.find((x) => x.name === group.groupName);
          if (!columnGroup) return;

          const groupColumnIds = columnGroup.columns.map((c) => c.fieldId);
          group.isErrored = state.fields.some((x) => x.isErrored && groupColumnIds.includes(x.columnMetadataFieldId));
        });
      }
    },
    // Fields value
    SetFieldValue: (state, action: PayloadAction<FieldValueModel>) => {
      const newField = action.payload;
      // get field
      const indexOfField = state.fields.findIndex(
        (f) =>
          f.columnMetadataFieldId === newField.columnMetadataFieldId && f.placeholderRowId === newField.placeholderRowId
      );
      if (indexOfField > -1) {
        // update value
        state.fields[indexOfField].value = newField.value;
        // compute errors
        computeAllErrors(state, indexOfField);
      }
    },
    // Tab management
    SetSelectedTab: (state, action: PayloadAction<string>) => {
      state.selectedTabValue = action.payload;
      // when switching tab, re-compute visibilityModel
      state.tableVisibilityModel = computeVisibilityModel(
        state.columnGroups,
        state.filtersColumnVisibilityMask,
        action.payload
      );
    },
    // Column filters management
    ToggleColumnShow: (state, action: PayloadAction<number>) => {
      // use function
      toggleColumnShow(state, action.payload);
    },
    ResetColumnHiddenToDefault: (state) => {
      // compute initial visibility mask
      state.filtersColumnVisibilityMask = initFiltersColumnVisibilityMask(state.columnGroups);
      // compute visibility model from selected tab and new visibility mask
      state.tableVisibilityModel = computeVisibilityModel(
        state.columnGroups,
        state.filtersColumnVisibilityMask,
        state.selectedTabValue!
      );
    },
    // Cell actions
    UpdateRowsBelow: (state, action: PayloadAction<{ columnMetadataFieldId: number; placeholderRowId: number }>) => {
      const { columnMetadataFieldId, placeholderRowId } = action.payload;

      const fieldToCopy = state.fields.find(
        (f) => f.columnMetadataFieldId === columnMetadataFieldId && f.placeholderRowId === placeholderRowId
      );
      // get field value to copy
      const valueToCopy = fieldToCopy?.value;
      if (fieldToCopy && valueToCopy) {
        let placeholdersToUpdateIds: number[] = [];

        // get ids of placeholders to update , starting from last and stopping to target one
        for (let i = state.rows.length - 1; i >= 0; i--) {
          const element = state.rows[i];
          if (element.id === placeholderRowId) break;

          placeholdersToUpdateIds.push(element.id);
        }

        // loop over placeholders to update
        if (placeholdersToUpdateIds.length > 0) {
          // get fields to update
          const fieldsToUpdate = state.fields.filter(
            (f) =>
              f.columnMetadataFieldId === columnMetadataFieldId &&
              placeholdersToUpdateIds.includes(f.placeholderRowId) &&
              f.isApplicable
          );

          // loop over fields
          for (let i = 0; i < fieldsToUpdate.length; i++) {
            const element = fieldsToUpdate[i];
            // get field index
            const index = state.fields.findIndex(
              (f) =>
                f.columnMetadataFieldId === element.columnMetadataFieldId &&
                f.placeholderRowId === element.placeholderRowId
            );
            if (index > -1) {
              updateFieldValueFromOtherField(state, fieldToCopy, valueToCopy, index);
            }
          }
        }
      }
    },
    UpdateAllRows: (state, action: PayloadAction<{ columnMetadataFieldId: number; placeholderRowId: number }>) => {
      const { columnMetadataFieldId, placeholderRowId } = action.payload;
      const fieldToCopy = state.fields.find(
        (f) => f.columnMetadataFieldId === columnMetadataFieldId && f.placeholderRowId === placeholderRowId
      );
      // get value to copy
      const valueToCopy = fieldToCopy?.value;
      // if value can be copied (not null or undefined)
      if (fieldToCopy && valueToCopy) {
        // gets fields to update
        const fieldsToUpdate = state.fields.filter(
          (f) => f.columnMetadataFieldId === columnMetadataFieldId && f.isApplicable
        );
        // loop over fields
        for (let i = 0; i < fieldsToUpdate.length; i++) {
          const element = fieldsToUpdate[i];
          // get index
          const index = state.fields.findIndex(
            (f) =>
              f.columnMetadataFieldId === element.columnMetadataFieldId &&
              f.placeholderRowId === element.placeholderRowId
          );
          if (index > -1) {
            // field is multi
            updateFieldValueFromOtherField(state, fieldToCopy, valueToCopy, index);
          }
        }
      }
    },
    ResetFieldToDefault: (
      state,
      action: PayloadAction<{ columnMetadataFieldId: number; placeholderRowId: number }>
    ) => {
      const { columnMetadataFieldId, placeholderRowId } = action.payload;
      // get field index
      const fieldIndex = state.fields.findIndex(
        (f) => f.columnMetadataFieldId === columnMetadataFieldId && f.placeholderRowId === placeholderRowId
      );
      if (fieldIndex > -1) {
        // compute inital meta and value of field from
        const initialValue = getComputedMetaAndInitialValue(
          state.fields[fieldIndex].predefinedValues,
          state.fields[fieldIndex].metadata
        ).value;
        // set initialValue
        state.fields[fieldIndex].value = initialValue;
        // compute errors
        computeAllErrors(state, fieldIndex);
      }
    },
    DuplicateRow: (state, action: PayloadAction<{ rowId: number; name: string }>) => {
      const { rowId, name } = action.payload;
      // get placeholder index
      const rowIndexToDuplicate = state.rows.findIndex((r) => r.id === rowId);

      if (rowIndexToDuplicate > -1) {
        const placeholderIndexToDuplicate = state.placeholders.findIndex(
          (p) => p.id === state.rows[rowIndexToDuplicate].originalPlaceholderId
        );

        if (placeholderIndexToDuplicate > -1) {
          // get placeholder
          const placeholderToDuplicate = state.placeholders[placeholderIndexToDuplicate];
          // create newId
          const newId = Math.max(...state.placeholders.map((p) => p.id).concat(state.rows.map((r) => r.id))) + 1;
          // create new placeholder
          const newPlaceholder = {
            ...new DbIngestConfigPackPlaceholderRowInfos({
              ...placeholderToDuplicate,
              id: newId,
              isOptionnal: true,
              name: name,
            }),
          };
          // get new row and fields
          const { rows, fields } = getRowsAndFieldsFromPlaceholderRows(
            state.columnGroups,
            [newPlaceholder],
            placeholderToDuplicate.id
          );
          // insert row in array
          state.rows.splice(rowIndexToDuplicate + 1, 0, rows[0]);

          // get fields to duplicate
          const fieldsToDuplicate = state.fields.filter((f) => f.placeholderRowId === rowId);
          // add fields
          fields.forEach((field) => {
            const fieldToDuplicateIndex = fieldsToDuplicate.findIndex(
              (f) => f.columnMetadataFieldId === field.columnMetadataFieldId
            );
            if (fieldToDuplicateIndex > -1) {
              field.value = fieldsToDuplicate[fieldToDuplicateIndex].value;
            }
            state.fields.push(field);
          });

          // Compute field.isErrored BEFORE init errors of groups etc (which is based on field.isErrored)
          const allColumns = state.columnGroups.flatMap((g) => g.columns);
          fields.forEach((field) => {
            field.isErrored = computeFieldIsErroredAdvanced(field, fields, allColumns);
          });

          // compute errors
          const { groupsErrors, columnsErrors, rowErrors } = initErrors(state.columnGroups, rows, fields);
          // add errors to groups
          groupsErrors.forEach((groupError) => {
            const indexOfGroupError = state.groupErrors.findIndex((ge) => ge.groupName === groupError.groupName);
            if (indexOfGroupError > -1)
              state.groupErrors[indexOfGroupError].isErrored =
                state.groupErrors[indexOfGroupError].isErrored || groupError.isErrored;
          });
          state.groupedRowErrors.push(...rowErrors);
          columnsErrors.forEach((columnError) => {
            const indexOfColumnError = state.columnErrors.findIndex(
              (c) => c.columnMetadataFieldId === columnError.columnMetadataFieldId
            );
            if (indexOfColumnError > -1) state.columnErrors[indexOfColumnError].errorNumber += columnError.errorNumber;
          });
        }
      }
    },
    GenerateAssets: (state) => {
      // reset some props of state
      state.generatedAssets = [];
      state.creationProgress.progress = 0;
      state.creationProgress.total = 0;
      state.creationErrors = [];
      state.errorMessage = "";

      const rows = state.rows;
      const fields = state.fields;

      // loop over rows
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        // get fields
        const rowFields = fields.filter((f) => f.placeholderRowId === row.id);
        // create new asset
        const newAsset: DbAssetObjectInfos = {
          ...new DbAssetObjectInfos({
            id: i,
            type: rowFields.find((f) => f.metadata && f.metadata.tagName! === "objectType")?.value.value as ObjectType,
            layer: ObjectLayer.Asset,
            classification: rowFields.find((f) => f.metadata && f.metadata.tagName! === "objectClass")?.value.value,
            providerId: Number(
              rowFields.find((f) => f.metadata && f.metadata.tagName! === "labProviderId")?.value.value
            ),
            placeholderRowId: row.originalPlaceholderId,
            metadata: [],
            name: rowFields.find((f) => f.metadata && f.metadata.tagName! === "name")?.value || row.placeHolderName,
            hidden: false,
            isDeleted: false,
          }),
        };

        // loop over fields to add them as metas in new asset
        for (let j = 0; j < rowFields.length; j++) {
          const field = rowFields[j];

          if (!field.isApplicable || !field.metadata || field.columnMetadataFieldId <= 0 || !field.value) continue;

          // init value
          const val = getFieldValue(field);

          // add new meta to asset
          AddNewMetadataToAsset(newAsset, {
            id: field.columnMetadataFieldId,
            fieldName: field.metadata.tagName!,
            type: field.metadata.baseType!,
            value: val,
          });
        }
        // push assets
        state.generatedAssets.push(newAsset);
      }
      // set creation progress total
      state.creationProgress.total = state.generatedAssets.length * state.selectedTitles.length;
    },
    SetTitleSelectedEpisodes: (state, action: PayloadAction<{ titleId: number; selectedEpisodes: number[] }>) => {
      const { titleId, selectedEpisodes } = action.payload;
      // update title season selected episodes
      const indexOfTitle = state.selectedEpisodes.findIndex((t) => t.titleId === titleId);
      if (indexOfTitle > -1) state.selectedEpisodes[indexOfTitle].selectedEpisodes = selectedEpisodes;
    },
    SelectAllEpisodesOfTitle: (state, action: PayloadAction<number>) => {
      const indexOfTitle = state.selectedEpisodes.findIndex((t) => t.titleId === action.payload);
      // select all episodes of title
      if (indexOfTitle > -1)
        state.selectedEpisodes[indexOfTitle].selectedEpisodes = Array.from(
          {
            length: state.selectedEpisodes[indexOfTitle].totalEpisodes,
          },
          (item, index) => (item = index + 1)
        );
    },
    UnselectAllEpisodesOfTitle: (state, action: PayloadAction<number>) => {
      const indexOfTitle = state.selectedEpisodes.findIndex((t) => t.titleId === action.payload);
      if (indexOfTitle > -1) state.selectedEpisodes[indexOfTitle].selectedEpisodes = [];
    },
    IncrementProgress: (state) => {
      state.creationProgress = { progress: state.creationProgress.progress + 1, total: state.creationProgress.total };
    },
    AddCreateError: (state, action: PayloadAction<CreateAssetsErrorLog[]>) => {
      state.creationErrors.push(...action.payload);
    },
    AddCreateErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },
    // Reset state
    ResetReduxNewRequest: (state) => {
      state.isLoading = initialState.isLoading;
      state.selectedTitles = initialState.selectedTitles;
      state.fetchedPacks = initialState.fetchedPacks;
      state.selectedPack = initialState.selectedPack;
      state.placeholders = initialState.placeholders;
      state.columnGroups = initialState.columnGroups;
      state.linkedMetas = initialState.linkedMetas;
      state.rows = initialState.rows;
      state.fields = initialState.fields;
      state.groupErrors = initialState.groupErrors;
      state.columnErrors = initialState.columnErrors;
      state.groupedRowErrors = initialState.groupedRowErrors;
      state.selectedTabValue = initialState.selectedTabValue;
      state.filtersColumnVisibilityMask = initialState.filtersColumnVisibilityMask;
      state.tableVisibilityModel = initialState.tableVisibilityModel;
      state.generatedAssets = initialState.generatedAssets;
      state.creationProgress = initialState.creationProgress;
      state.creationErrors = initialState.creationErrors;
      state.errorMessage = initialState.errorMessage;
    },
    // Load/Save config
    SetBackups: (state, action: PayloadAction<DbIngestPersonalBackupInfos[]>) => {
      state.configBackups = action.payload;
    },
    DownloadTableAsCsv: (state) => {
      let data: string = "";
      const columns = state.columnGroups.flatMap((x) => x.columns);
      columns.forEach((col, index) => {
        data += col.label;
        if (index < columns.length - 1) data += GlobalConst.EXPORT_DELIMITER;
        else data += "\n";
      });
      state.rows.forEach((row, rowIndex) => {
        data += `${row.placeHolderName}${GlobalConst.EXPORT_DELIMITER}`;
        columns.forEach((col, colIndex) => {
          const field = state.fields.find(
            (f) => f.placeholderRowId === row.id && f.columnMetadataFieldId === col.metadata.id
          );
          if (!field || !field.metadata || !field.isApplicable) {
            data += "N/A";
          } else {
            let value = getFieldValue(field);
            let stop = 0; // in case we are trapped in a loop
            while (typeof value === "object" && stop < 10) {
              value = value.value;
              ++stop;
            }
            data += !value ? "" : `${value}`;
          }
          if (colIndex < columns.length - 1) data += GlobalConst.EXPORT_DELIMITER;
          else if (rowIndex < state.rows.length - 1) data += "\n";
        });
      });

      DownloadFile(data, "text/csv", "DF-Placeholders");
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NewRequestSliceData>) => {
    builder.addCase(FetchLightPackPlaceholders.fulfilled, (state, action) => {
      state.placeholders.push(...action.payload);
      state.isMarketplaceLoading = false;
    });
    builder.addCase(FetchNonOptionalPlaceholder.fulfilled, (state, action) => {
      // compute rows and fields of table
      const { rows, fields } = getRowsAndFieldsFromPlaceholderRows(state.columnGroups, action.payload);
      // compute errors
      const { groupsErrors, columnsErrors, rowErrors } = initErrors(state.columnGroups, rows, fields);

      state.rows = rows;
      state.fields = fields;
      state.groupErrors = groupsErrors;
      state.columnErrors = columnsErrors;
      state.groupedRowErrors = rowErrors;

      // show linked columns that have at least one error
      toggleColumnsVisibilityForLinkedMetas(state);

      state.isLoading = false;
    });
    builder.addCase(FetchPackAndTitlesThenFillWithBackup.fulfilled, (state, action) => {
      if (!action.payload || !action.payload.placeholders || !action.payload.backupId) return;
      const backupIndex = state.configBackups.findIndex((b) => b.id === action.payload.backupId);

      if (backupIndex > -1) {
        const nameMetaId = state.fetchedColumns.find((m) => m.columnFieldInfos?.tagName === "name")?.columnFieldInfos
          ?.id;

        const backup = state.configBackups[backupIndex]; // Get backup
        // Init filters
        const filtersModel = initFiltersColumnVisibilityMask(state.columnGroups);
        filtersModel.forEach((filter) => {
          if (backup.showColumIds)
            if (backup.showColumIds.includes(filter.columnId)) filter.show = true;
            else filter.show = false;
        });

        const backupSelectedRowsIds = backup.placeholderRowsSelected?.flatMap((r) => r.rowId!) || []; // Get placeholders ids
        const initialPlaceholders =
          action.payload.placeholders.filter((r) => backupSelectedRowsIds.includes(r.id)) || []; // Get placeholders

        // compute rows and fields of initial placeholders
        const { rows, fields } = getRowsAndFieldsFromPlaceholderRows(state.columnGroups, initialPlaceholders);

        const duplicatesSelectedRowIds = FindDuplicates(backupSelectedRowsIds);
        // Loop on duplicates (duplicated rows)
        for (let i = 0; i < duplicatesSelectedRowIds.length; i++) {
          const duplicateRowId = duplicatesSelectedRowIds[i].value;
          const duplicatedRows = backup.placeholderRowsSelected?.filter((r) => r.rowId === duplicateRowId);

          const placeholderIndexToDuplicate = initialPlaceholders.findIndex((p) => p.id === duplicateRowId);
          if (placeholderIndexToDuplicate === -1) continue;

          const rowsToInsert: NewRequestRow[] = [];
          const fieldsToInsert: NewRequestField[] = [];

          for (let j = 0; j < duplicatesSelectedRowIds[i].count; j++) {
            const duplicatedRow = duplicatedRows?.at(j);
            if (!duplicatedRow) continue;

            const placeholderToDuplicate = initialPlaceholders[placeholderIndexToDuplicate];
            if (duplicatedRow.frontId === placeholderToDuplicate.id) continue; // Bypass original placeholders cause already added

            const newPlaceholder = {
              ...new DbIngestConfigPackPlaceholderRowInfos({
                ...placeholderToDuplicate,
                id:
                  duplicatedRow.frontId ||
                  Math.max(
                    ...(action.payload.placeholders || [])
                      .map((r) => r.id)
                      .concat(
                        (backup.placeholderRowsSelected || [])
                          .filter((s) => s.frontId !== undefined && s.frontId !== null)
                          .map((s) => s.frontId!)
                      )
                  ) + 1,
                isOptionnal: true,
                name: duplicatedRow.name || duplicatedRow.metadata?.find((m) => m.id === nameMetaId)?.value || "N/A",
              }),
            };
            const { rows, fields } = getRowsAndFieldsFromPlaceholderRows(
              state.columnGroups,
              [newPlaceholder],
              placeholderToDuplicate.id
            );
            rowsToInsert.push(...rows);
            fieldsToInsert.push(...fields);
          }
          const rowIndexForInsert = rows.findIndex((r) => r.id === duplicateRowId);
          rows.splice(rowIndexForInsert + 1, 0, ...rowsToInsert);
          fields.push(...fieldsToInsert);
        }

        // add values for restored placeholders
        backup.placeholderRowsSelected?.forEach((row) => {
          row.metadata?.forEach((meta) => {
            const fieldIndex = fields.findIndex(
              (f) => f.placeholderRowId === row.frontId && f.columnMetadataFieldId === meta.id
            );
            if (fieldIndex > -1) {
              if (
                fields[fieldIndex].metadata?.baseType === BaseMetadataType.DbStringMultiMetadataValueInfos ||
                fields[fieldIndex].metadata?.baseType === BaseMetadataType.DbLongMultiMetadataValueInfos ||
                fields[fieldIndex].metadata?.baseType === BaseMetadataType.DbDoubleMultiMetadataValueInfos
              ) {
                const savedValues = meta.value?.split(GlobalConst.EXPORT_DELIMITER);
                fields[fieldIndex].value = fields[fieldIndex].metadata?.values?.filter(
                  (v) => v.value && savedValues?.includes(v.value)
                );
                if (!fields[fieldIndex].isValueSettable && (savedValues?.length ?? 0) <= 1)
                  fields[fieldIndex].value = fields[fieldIndex].value?.[0]?.value || undefined;
              } else
                fields[fieldIndex].value =
                  fields[fieldIndex].metadata?.values?.find((v) => v.value === meta.value) || meta.value || undefined;
            }
          });
        });

        // Compute errors AFTER all values are setted in fields (cause some fields depends on other ones)
        const allColumns = state.columnGroups.flatMap((g) => g.columns);
        fields.forEach((field) => {
          field.isErrored = computeFieldIsErroredAdvanced(field, fields, allColumns);
        });

        // compute errors
        const { groupsErrors, columnsErrors, rowErrors } = initErrors(state.columnGroups, rows, fields);

        // Process outdated placeholders => if a config was saved before some placeholder got updated, mark saved placeholder as outdated
        rows.forEach((row) => {
          const placeholderIndex = initialPlaceholders.findIndex((p) => p.id === row.originalPlaceholderId);
          if (placeholderIndex > -1) {
            const originalPlaceholder = initialPlaceholders[placeholderIndex];
            if (
              backup.creationInfos &&
              backup.creationInfos?.date !== null &&
              originalPlaceholder.lastUpdate !== null &&
              backup.creationInfos.date < originalPlaceholder.lastUpdate
            )
              row.isOutdated = true;
          }
        });

        // set state
        state.isLoading = false;
        state.rows = rows;
        state.fields = fields;
        state.groupErrors = groupsErrors;
        state.columnErrors = columnsErrors;
        state.groupedRowErrors = rowErrors;
      }
    });
    builder.addCase(FetchFullPlaceholdersByIds.fulfilled, (state, action) => {
      const { rows: rowsToAdd, fields: fieldsToAdd } = getRowsAndFieldsFromPlaceholderRows(
        state.columnGroups,
        action.payload
      );

      // add rows
      state.rows.push(...rowsToAdd);
      // add fields
      state.fields.push(...fieldsToAdd);

      // compute errors
      const { groupsErrors, columnsErrors, rowErrors } = initErrors(state.columnGroups, rowsToAdd, fieldsToAdd);

      // add errors to groups
      if (!state.groupErrors.length)
        state.groupErrors.push(...groupsErrors); // [LB] DF-5161 : In case groupErrors are not already initialized
      else
        groupsErrors.forEach((groupError) => {
          const indexOfGroupError = state.groupErrors.findIndex((ge) => ge.groupName === groupError.groupName);
          if (indexOfGroupError > -1)
            state.groupErrors[indexOfGroupError].isErrored =
              state.groupErrors[indexOfGroupError].isErrored || groupError.isErrored;
        });

      // push errors in rows error model array (new rows)
      state.groupedRowErrors.push(...rowErrors);

      // add errors to existing columns error model
      if (!state.columnErrors.length)
        state.columnErrors.push(...columnsErrors); // [LB] DF-5161 : In case groupErrors are not already initialized
      else
        columnsErrors.forEach((columnError) => {
          const indexOfColumnError = state.columnErrors.findIndex(
            (c) => c.columnMetadataFieldId === columnError.columnMetadataFieldId
          );
          if (indexOfColumnError > -1) state.columnErrors[indexOfColumnError].errorNumber += columnError.errorNumber;
        });

      toggleColumnsVisibilityForLinkedMetas(state);
    });
    builder.addCase(RefreshBackups.fulfilled, (state, action) => {
      if (action.payload) state.configBackups = action.payload;
    });
    builder.addCase(FetchSelectedTitles.fulfilled, (state, action) => setSelectedTitlesToState(state, action));
  },
});

export const {
  SetIsLoading,
  SetIsMarketplaceLoading,
  SetSelectedTitles,
  RemoveTitleByName,
  SetFetchedPacks,
  SetFetchedColumnGroups,
  SelectPackById,
  UnselectPack,
  UnselectPlaceholderById,
  SetFieldValue,
  SetSelectedTab,
  ToggleColumnShow,
  ResetColumnHiddenToDefault,
  UpdateRowsBelow,
  UpdateAllRows,
  ResetFieldToDefault,
  DuplicateRow,
  GenerateAssets,
  SetTitleSelectedEpisodes,
  SelectAllEpisodesOfTitle,
  UnselectAllEpisodesOfTitle,
  IncrementProgress,
  AddCreateError,
  AddCreateErrorMessage,
  ResetReduxNewRequest,
  SetBackups,
  DownloadTableAsCsv,
} = newRequestSlice.actions;

export const GetIsLoading = (state: RootState): boolean => state.newRequestSlice.isLoading;

export const GetIsMarketplaceLoading = (state: RootState): boolean => state.newRequestSlice.isMarketplaceLoading;

export const GetSelectedTitles = (state: RootState): DbAssetObjectInfos[] => state.newRequestSlice.selectedTitles;

export const GetFetchedPacks = (state: RootState): DbIngestConfigPackInfos[] => state.newRequestSlice.fetchedPacks;

export const GetSelectedPack = (state: RootState): DbIngestConfigPackInfos | undefined =>
  state.newRequestSlice.selectedPack;

export const GetNotSelectedPlaceholders = (state: RootState): DbIngestConfigPackPlaceholderRowInfos[] => {
  const selectedPlaceholderIds = state.newRequestSlice.rows.flatMap((r) => r.id);
  return state.newRequestSlice.placeholders.filter((x) => !selectedPlaceholderIds.includes(x.id)) || [];
};

export const GetColumnGroups = (state: RootState): NewRequestColumnGroup[] => state.newRequestSlice.columnGroups;

export const GetRows = (state: RootState): NewRequestRow[] => state.newRequestSlice.rows;

export const GetFieldValueCached = (
  state: RootState,
  columnMetadataFieldId: number | undefined,
  placeholderRowId: number | undefined
) =>
  state.newRequestSlice.fields.find(
    (f) => f.columnMetadataFieldId === columnMetadataFieldId && f.placeholderRowId === placeholderRowId
  )?.value || null;

export const GetFieldValue =
  (columnMetadataFieldId: number | undefined, placeholderRowId: number | undefined) =>
  (state: RootState): any | null => {
    if (!columnMetadataFieldId || !placeholderRowId) return null;

    return (
      state.newRequestSlice.fields.find(
        (f) => f.columnMetadataFieldId === columnMetadataFieldId && f.placeholderRowId === placeholderRowId
      )?.value || null
    );
  };

export const GetFieldIsErroredCached = (
  state: RootState,
  columnMetadataFieldId: number | undefined,
  placeholderRowId: number | undefined
) =>
  state.newRequestSlice.fields.find(
    (f) => f.columnMetadataFieldId === columnMetadataFieldId && f.placeholderRowId === placeholderRowId
  )?.isErrored || false;

export const GetColumnErrorNumberCached = (state: RootState, columnMetadataFieldId: number) => {
  return (
    state.newRequestSlice.columnErrors.find((c) => c.columnMetadataFieldId === columnMetadataFieldId)?.errorNumber ?? 0
  );
};

export const GetColumnErrorNumber =
  (columnMetadataFieldId: number) =>
  (state: RootState): number => {
    const indexOfColumnError = state.newRequestSlice.columnErrors.findIndex(
      (c) => c.columnMetadataFieldId === columnMetadataFieldId
    );
    if (indexOfColumnError > -1) return state.newRequestSlice.columnErrors[indexOfColumnError].errorNumber;
    return 0;
  };

export const GetRowGroupedErrorNumberCached = (state: RootState, columnsIds: number[], placeholderRowId: number) => {
  return (
    state.newRequestSlice.groupedRowErrors.find(
      (r) => r.placeholderRowId === placeholderRowId && r.columnMetadataFieldIds.includes(columnsIds[0])
    )?.errorNumber ?? 0
  );
};

export const GetRowGroupedErrorNumber =
  (columnsIds: number[], placeholderRowId: number) =>
  (state: RootState): number => {
    const indexOfRow = state.newRequestSlice.groupedRowErrors.findIndex(
      (r) => r.placeholderRowId === placeholderRowId && r.columnMetadataFieldIds.includes(columnsIds[0])
    );
    if (indexOfRow > -1) return state.newRequestSlice.groupedRowErrors[indexOfRow].errorNumber;
    return 0;
  };

// TODO : change this by a var in state that we change on computing errors
export const GetIsTableErrored = (state: RootState): boolean => {
  return (
    state.newRequestSlice.columnErrors.some((c) => c.errorNumber > 0) ||
    state.newRequestSlice.groupedRowErrors.some((r) => r.errorNumber > 0)
  );
};

export const GetSelectedTabValue = (state: RootState) => state.newRequestSlice.selectedTabValue;

export const GetFiltersVisibilityModel = (state: RootState): FiltersVisibilityModel[] =>
  state.newRequestSlice.filtersColumnVisibilityMask;

export const GetTableVisibilityModel = (state: RootState): GridColumnVisibilityModel =>
  state.newRequestSlice.tableVisibilityModel;

export const GetTopologyScheme = (state: RootState): TitleTopology[] => {
  let output: TitleTopology[] = [];
  if (!state.newRequestSlice.generatedAssets) return [];
  // compute topology scheme

  // for each title
  state.newRequestSlice.selectedTitles.forEach((title) => {
    // push object with infos
    output.push({
      id: title.id,
      name: title.name,
      contents:
        title.childObjects
          ?.filter((c) => c.layer === ObjectLayer.Content)
          .map((content) => ({ name: content.name, isNew: false, assets: [] })) || [],
      isSeason: GetMetadataByTagName(title, "titleType")?.value === "Season",
      totalEpisodes: GetMetadataByTagName(title, "serieSeasonEpisodeTotalNumber")?.value || 0,
    });
  });

  // group asset by content name
  const assetsGroupedByToMap = GroupByToMap(
    state.newRequestSlice.generatedAssets,
    (a) => a.metadata?.find((m) => m.fieldName === "contentType")?.value || "Other"
  );

  // loop on groups
  assetsGroupedByToMap.forEach((assets, contentName) => {
    // init assets topology objects
    const assetsTopology: AssetTopology[] = assets.map((asset, index) => ({
      id: index,
      placeholderRowId: asset.placeholderRowId ?? -1,
      name: asset.name,
      type: asset.type,
      isNew: true,
    }));

    // loop over titles
    output.forEach((title) => {
      // get content of title that match group name and extract it's childrens placeholderRowIds
      const existingAssetsPlaceholdersIdsOfContent =
        state.newRequestSlice.selectedTitles
          .find((t) => t.id === title.id)
          ?.childObjects?.find((c) => c.layer === ObjectLayer.Content && c.name === contentName)
          ?.childObjects?.flatMap((c) => c.placeholderRowId) || [];

      // for each asset set isNew prop (needed in datagrid of topology)
      assetsTopology.forEach((asset) => {
        asset["isNew"] =
          !Array.isArray(existingAssetsPlaceholdersIdsOfContent) ||
          !existingAssetsPlaceholdersIdsOfContent.includes(asset.placeholderRowId);
      });
      // get content index
      const contentIndex = title.contents.findIndex((c) => c.name === contentName);
      // if content exists, set assets
      if (contentIndex > -1) title.contents[contentIndex].assets = assetsTopology;
      // else create new content
      else title.contents.push({ name: contentName, isNew: true, assets: assetsTopology });
    });
  });

  return output;
};

export const GetSelectedEpidodes = (state: RootState) => state.newRequestSlice.selectedEpisodes;

export const GetTitleSelectedEpisodes =
  (titleId: number) =>
  (state: RootState): number[] =>
    state.newRequestSlice.selectedEpisodes.find((o) => o.titleId === titleId)?.selectedEpisodes || [];

export const GetCreationProgress = (state: RootState): NewRequestProgress => state.newRequestSlice.creationProgress;

export const GetCreationErrors = (state: RootState): CreateAssetsErrorLog[] => state.newRequestSlice.creationErrors;

export const GetErrorMessage = (state: RootState): string => state.newRequestSlice.errorMessage;

export const GetConfigBackups = (state: RootState): DbIngestPersonalBackupInfos[] =>
  state.newRequestSlice.configBackups;

export const GetCanUpdateOrSaveConfig = (state: RootState): boolean =>
  !state.newRequestSlice.isLoading && state.newRequestSlice.selectedPack !== undefined;

export const GetCreatePersonalBackupsRequest = (state: RootState): CreatePersonalBackupsRequest => {
  const _state = state.newRequestSlice;

  const visibleColumnsIds = _state.filtersColumnVisibilityMask.filter((c) => c.show).flatMap((f) => f.columnId);

  let output: CreatePersonalBackupsRequest = new CreatePersonalBackupsRequest({
    packId: _state.selectedPack?.id,
    titleIds: _state.selectedTitles.map((t) => t.id),
    episodesListSelection: [], // TODO : change this as it only use the first title
    showColumIds: visibleColumnsIds,
    placeholderRowsSelected: _state.rows.map((row) => ({
      rowId: row.originalPlaceholderId,
      frontId: row.id,
      name: row.placeHolderName,
      metadata: _state.fields
        .filter((f) => f.placeholderRowId === row.id && f.metadata?.tagName !== "isSelected")
        .map((field) => {
          // Multiple values case
          let value = "";
          if (
            Array.isArray(field.value) &&
            (field.metadata?.baseType === BaseMetadataType.DbStringMultiMetadataValueInfos ||
              field.metadata?.baseType === BaseMetadataType.DbLongMultiMetadataValueInfos ||
              field.metadata?.baseType === BaseMetadataType.DbDoubleMultiMetadataValueInfos)
          ) {
            value = field.value.map((x) => x.value).join(GlobalConst.EXPORT_DELIMITER);
          } else value = field.value?.value || field.value;

          return {
            id: field.columnMetadataFieldId,
            value,
          };
        }),
    })),
  });

  return output;
};

export const GetGroupErrors = (state: RootState) => state.newRequestSlice.groupErrors;

export default newRequestSlice.reducer;
